import { nextTick, ref, watch, type Ref } from 'vue'
import { localHLC, repairStoredHlcs } from '../../services/sync/hlc'
import { syncEngine } from '../../services/sync/syncEngine'
import { migrateBarLayout } from '../../utils/barLayout'
import { LEGACY_BAR_KEYS, legacyBarSettings, savePersisted } from './load'
import { normalizeSettingValue } from './normalize'

// Saving the synced settings on this device, and keeping them in step with
// the cloud key by key: each key carries a hybrid logical clock (HLC) stamp,
// and the newer stamp wins.

const LOCAL_HLC_KEY = 'ink-settings-local-hlc'
const SYNCED_HLC_KEY = 'ink-settings-synced-hlc'

type RemoteSettings = Record<string, { value: any; hlc: number }>

/** Repairs per-key timestamps from a broken clock in place (see repairStoredHlcs). */
function repairSettingHlcs(local: Record<string, number>, synced: Record<string, number>) {
  let repaired = false
  for (const key of new Set([...Object.keys(local), ...Object.keys(synced)])) {
    const fixed = repairStoredHlcs(local[key] ?? 0, synced[key] ?? 0)
    if (!fixed) continue
    local[key] = fixed.hlc
    synced[key] = fixed.syncedHlc
    repaired = true
  }
  if (!repaired) return
  try {
    localStorage.setItem(LOCAL_HLC_KEY, JSON.stringify(local))
    localStorage.setItem(SYNCED_HLC_KEY, JSON.stringify(synced))
  } catch { /* The repair runs again on the next launch. */ }
}

/**
 * Saves `refMap` whenever any of it changes, and pushes the changes when
 * settings sync is on. Returns the ways settings come in from elsewhere.
 */
export function useSettingsSync(refMap: Record<string, Ref<any>>, syncSettingsEnabled: Ref<boolean>) {
  const currentLocalHlcs = ref<Record<string, number>>(
    JSON.parse(localStorage.getItem(LOCAL_HLC_KEY) || '{}')
  )
  const lastSyncedHlcs = ref<Record<string, number>>(
    JSON.parse(localStorage.getItem(SYNCED_HLC_KEY) || '{}')
  )
  repairSettingHlcs(currentLocalHlcs.value, lastSyncedHlcs.value)

  function saveHlcs() {
    localStorage.setItem(LOCAL_HLC_KEY, JSON.stringify(currentLocalHlcs.value))
    localStorage.setItem(SYNCED_HLC_KEY, JSON.stringify(lastSyncedHlcs.value))
  }

  let syncDebounceTimer: any = null

  // What each key held when the watcher last ran. A key that differs from
  // this is a local edit. The watcher runs on the next tick, after a remote
  // apply has finished, so remote values are recorded here as they are
  // applied (see markSeen) rather than skipped by a flag.
  let lastValues: Record<string, any> = {}
  for (const key of Object.keys(refMap)) {
    lastValues[key] = JSON.parse(JSON.stringify(refMap[key].value))
  }

  function markSeen(key: string) {
    lastValues[key] = JSON.parse(JSON.stringify(refMap[key].value))
  }

  watch(
    Object.values(refMap),
    () => {
      const nowHlc = localHLC.now()
      let anyChanged = false

      const newSettings: Record<string, any> = {}
      for (const [key, r] of Object.entries(refMap)) {
        newSettings[key] = r.value
      }

      for (const key of Object.keys(refMap)) {
        if (JSON.stringify(newSettings[key]) !== JSON.stringify(lastValues[key])) {
          currentLocalHlcs.value[key] = nowHlc
          anyChanged = true
        }
      }
      if (anyChanged) {
        localStorage.setItem(LOCAL_HLC_KEY, JSON.stringify(currentLocalHlcs.value))
      }

      lastValues = JSON.parse(JSON.stringify(newSettings))

      savePersisted(newSettings)

      // Debounce and push settings changes to Cloudflare syncEngine if sync is enabled
      if (anyChanged && syncSettingsEnabled.value) {
        if (syncDebounceTimer) clearTimeout(syncDebounceTimer)
        syncDebounceTimer = setTimeout(() => {
          const hlcTs = localHLC.now()
          const changes = Object.entries(newSettings).map(([key, value]) => ({
            key,
            value,
            hlc: currentLocalHlcs.value[key] || hlcTs,
          }))

          syncEngine.pushSettings(changes)
        }, 500)
      }
    },
    { deep: true }
  )

  /**
   * Builds the bar layouts from the old toolbar settings in a blob that has
   * them but no layouts. A device with no saved settings starts from the
   * default layouts, so synced settings may replace those (untouched ones
   * only); imported settings always do, as any import does.
   */
  function migrateBarLayoutsFrom(values: Record<string, any>, onlyIfUntouched: boolean) {
    if (!LEGACY_BAR_KEYS.some((key) => values[key] != null)) return
    for (const [device, key] of [['desktop', 'desktopBarLayout'], ['phone', 'phoneBarLayout']] as const) {
      if (values[key] != null || (onlyIfUntouched && currentLocalHlcs.value[key])) continue
      refMap[key].value = migrateBarLayout(device, legacyBarSettings(values))
      // Built from synced values, which every device can build alike.
      if (onlyIfUntouched) markSeen(key)
    }
  }

  function migrateBarLayoutsFromRemote(remote: RemoteSettings) {
    migrateBarLayoutsFrom(Object.fromEntries(Object.entries(remote).map(([key, item]) => [key, item?.value])), true)
  }

  /** Takes a remote value: the cloud's copy and this device's now agree on it. */
  function applyRemote(key: string, item: { value: any; hlc: number }) {
    refMap[key].value = normalizeSettingValue(key, item.value)
    markSeen(key)
    lastSyncedHlcs.value[key] = item.hlc
    currentLocalHlcs.value[key] = item.hlc
  }

  /**
   * Applies remote settings pulled from Cloudflare Worker, respecting HLC timestamps.
   */
  function applyRemoteSettings(remoteSettings: RemoteSettings) {
    if (!remoteSettings || typeof remoteSettings !== 'object') return
    for (const [key, item] of Object.entries(remoteSettings)) {
      if (!item || item.value === undefined || item.value === null) continue
      if (!refMap[key]) continue

      const localHlc = currentLocalHlcs.value[key] || 0
      const lastSyncedHlc = lastSyncedHlcs.value[key] || 0

      // If local has not changed since last sync, or remote is newer than local change
      if (localHlc <= lastSyncedHlc || item.hlc > localHlc) applyRemote(key, item)
    }
    migrateBarLayoutsFromRemote(remoteSettings)
    saveHlcs()
  }

  /**
   * Pulled remote settings from cloud, performs per-key LWW resolution,
   * overwriting safe local defaults while keeping and pushing back any pre-login changes.
   * Pass a pull that is already in hand to reuse it; otherwise this pulls on
   * its own. Returns whether the remote values were applied.
   */
  async function syncSettingsWithCloud(prePulled?: RemoteSettings): Promise<boolean> {
    if (!syncSettingsEnabled.value || !syncEngine.isAuthenticated()) return false

    try {
      const remoteSettings = prePulled ?? await syncEngine.pullSettings()
      if (!remoteSettings) return false

      const localWins: { key: string; value: any; hlc: number }[] = []

      for (const key of Object.keys(refMap)) {
        const remoteItem = remoteSettings[key]
        const localHlc = currentLocalHlcs.value[key] || 0
        const lastSyncedHlc = lastSyncedHlcs.value[key] || 0

        if (localHlc > lastSyncedHlc) {
          // Local wins! Prepare to push it to the cloud.
          localWins.push({
            key,
            value: refMap[key].value,
            hlc: localHlc,
          })
          lastSyncedHlcs.value[key] = localHlc // Mark local as synced
        } else if (remoteItem && remoteItem.value !== undefined && remoteItem.value !== null) {
          applyRemote(key, remoteItem)
        }
      }
      migrateBarLayoutsFromRemote(remoteSettings)
      saveHlcs()

      if (localWins.length > 0) {
        await syncEngine.pushSettings(localWins)
      }
      return true
    } catch (e) {
      console.warn('[SettingsStore] Settings sync with cloud failed:', e)
      return false
    }
  }

  /** Plain snapshot of every persisted setting, keyed as in local storage. */
  function exportableSettings(): Record<string, any> {
    const values: Record<string, any> = {}
    for (const [key, r] of Object.entries(refMap)) {
      values[key] = JSON.parse(JSON.stringify(r.value))
    }
    return values
  }

  /**
   * Applies settings carried over by a corpus import. They are treated as
   * local edits: each changed key gets a fresh HLC, so the imported values
   * win over the cloud copy instead of being overwritten by it.
   */
  async function applyImportedSettings(values: Record<string, any>): Promise<void> {
    for (const [key, value] of Object.entries(values)) {
      const target = refMap[key]
      if (!target || value === undefined || value === null) continue
      target.value = normalizeSettingValue(key, value)
    }
    migrateBarLayoutsFrom(values, false)
    // Let the persistence watcher stamp HLCs and write local storage, then
    // push immediately rather than waiting for its debounce.
    await nextTick()
    if (syncSettingsEnabled.value) {
      if (syncDebounceTimer) clearTimeout(syncDebounceTimer)
      syncDebounceTimer = null
      const hlcTs = localHLC.now()
      await syncEngine.pushSettings(Object.entries(exportableSettings()).map(([key, value]) => ({
        key,
        value,
        hlc: currentLocalHlcs.value[key] || hlcTs,
      })))
    }
  }

  return { applyRemoteSettings, syncSettingsWithCloud, exportableSettings, applyImportedSettings }
}
